import { format, differenceInDays, isValid, parseISO } from 'date-fns'

interface UplistingPhotoAttributes {
  large_url?: string
  medium_url?: string
  url?: string
  original_url?: string
  thumb_url?: string
}

interface UplistingPhoto {
  attributes?: UplistingPhotoAttributes
  url?: string
}

interface PropertyLike {
  external?: {
    uplisting?: {
      attributes?: {
        nickname?: string
        name?: string
      }
      relationships?: {
        photos?: {
          data?: (UplistingPhoto | string)[]
        }
      }
    }
  }
  display_name?: string
  name?: string
  cover_photo_url?: string
}

interface ReservationLike {
  external?: {
    uplisting?: {
      preferred_guest_name?: string
      guest_name?: string
    }
  }
  guest_name?: string
}

interface MessageLike {
  channel?: string
  source?: string
  from?: string
}

/**
 * Resolve a property's display name from whichever field is populated.
 * Shared by property-details.jsx and property-messages.jsx so both pages
 * agree on the same fallback order.
 * @param propertyId - used for the final "Property {id}" fallback
 */
export function getPropertyDisplayName(
  property: PropertyLike | null | undefined,
  propertyId: string
): string {
  if (!property) return `Property ${propertyId}`
  const attrs = property.external?.uplisting?.attributes || {}
  return (
    attrs.nickname ||
    attrs.name ||
    property.display_name ||
    property.name ||
    `Property ${propertyId}`
  )
}

/**
 * Resolve a reservation's guest display name from whichever field is
 * populated, preferring Uplisting's preferred_guest_name. Shared by
 * property-messages.jsx and inbox.jsx so every messages surface agrees on
 * the same fallback order.
 */
export function getGuestDisplayName(reservation: ReservationLike | null | undefined): string {
  const uplistingData = reservation?.external?.uplisting || {}
  return (
    uplistingData.preferred_guest_name ||
    reservation?.guest_name ||
    uplistingData.guest_name ||
    'Guest'
  )
}

/**
 * Get cover photo URL from property. Prefers cover_photo_url from the API
 * response, falls back to extracting from external.uplisting relationships
 * (legacy support). Shared by properties.jsx and property-messages.jsx so
 * both pages resolve a property's thumbnail the same way (Codex PR review:
 * property-messages.jsx originally only checked cover_photo_url, silently
 * losing the thumbnail for any property whose photo only exists in the
 * legacy relationships shape, even though the same property already shows a
 * real image on the properties list).
 */
export function getPropertyPhotoUrl(property: PropertyLike | null | undefined): string | null {
  if (!property) return null

  if (property.cover_photo_url) {
    return property.cover_photo_url
  }

  const photos = property?.external?.uplisting?.relationships?.photos?.data
  if (!photos || !Array.isArray(photos) || photos.length === 0) {
    return null
  }

  const firstPhoto = photos[0]

  if (typeof firstPhoto !== 'string' && firstPhoto.attributes) {
    return (
      firstPhoto.attributes.large_url ||
      firstPhoto.attributes.medium_url ||
      firstPhoto.attributes.url ||
      firstPhoto.attributes.original_url ||
      firstPhoto.attributes.thumb_url ||
      null
    )
  }

  if (typeof firstPhoto === 'string') {
    return firstPhoto
  }

  if (firstPhoto.url) {
    return firstPhoto.url
  }

  return null
}

/**
 * Format time string (HH:mm:ss) to AM/PM without seconds
 * @param timeString - Time in format "HH:mm:ss" or "HH:mm"
 * @returns Formatted time like "4:00 PM"
 */
export function formatTime(timeString: string): string {
  if (!timeString) return ''
  try {
    // Parse time string and format using date-fns
    // Create a date with today's date and the parsed time
    const today = new Date()
    const [hours, minutes] = timeString.split(':').map(Number)
    if (isNaN(hours) || isNaN(minutes)) return timeString

    const dateWithTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes
    )
    return format(dateWithTime, 'h:mm a')
  } catch {
    return timeString
  }
}

/**
 * Format date string to readable date
 * @param dateString - ISO date string
 * @returns Formatted date like "Jan 15, 2024"
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '—'
  try {
    return format(parseISO(dateString), 'MMM d, yyyy')
  } catch {
    return dateString
  }
}

/**
 * Format ISO datetime string to date + time (no seconds)
 * @param isoString - ISO datetime string
 * @returns Formatted like "Jan 15, 2024 4:00 PM"
 */
export function formatISODateTime(isoString: string): string {
  if (!isoString) return '—'
  try {
    return format(new Date(isoString), 'MMM d, yyyy h:mm a')
  } catch {
    return isoString
  }
}

/**
 * Format an ISO timestamp using the current browser locale.
 * @param value - ISO timestamp or Date
 * @returns Localized date-time, or fallback on invalid input
 */
export function formatLocaleDateTime(
  value: string | Date | null | undefined,
  options: { fallback?: string | null } = {}
): string | null {
  const { fallback = '—' } = options
  if (!value) return fallback
  try {
    const date = value instanceof Date ? value : parseISO(String(value))
    if (!isValid(date)) return fallback
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date)
  } catch {
    return fallback
  }
}

/**
 * Format date range for display in chips
 * @param checkIn - Check-in date
 * @param checkOut - Check-out date
 * @returns Formatted like "Jan 15 → Jan 18"
 */
export function formatDateRange(checkIn: string, checkOut: string): string {
  if (!checkIn || !checkOut) return '—'
  try {
    const start = format(parseISO(checkIn), 'MMM d')
    const end = format(parseISO(checkOut), 'MMM d')
    return `${start} → ${end}`
  } catch {
    return '—'
  }
}

/**
 * Format date range with day of week for reservation dates
 * @param checkIn - Check-in date
 * @param checkOut - Check-out date
 * @returns Formatted like "Mon, Jun 10 → Thu, Jun 13"
 */
export function formatDateRangeWithDay(checkIn: string, checkOut: string): string {
  if (!checkIn || !checkOut) return '—'
  try {
    const start = format(parseISO(checkIn), 'EEE, MMM d')
    const end = format(parseISO(checkOut), 'EEE, MMM d')
    return `${start} → ${end}`
  } catch {
    return '—'
  }
}

/**
 * Format currency amount
 * @param amount - Amount to format
 * @param currency - ISO 4217 currency code (Codex PR review: the pricing-calendar
 *   response can carry a non-USD currency; defaults to USD for every pre-existing caller)
 * @returns Formatted currency like "$1,234.56"
 */
export function formatCurrency(amount: number | null | undefined, currency = 'USD'): string {
  if (amount === null || amount === undefined) return '—'
  try {
    return currencyFormatter(currency).format(amount)
  } catch {
    return `$${amount}`
  }
}

// Building an Intl.NumberFormat costs far more than using one: the portfolio
// calendar formats a payout for every stay it lays out (CAP-1416).
const currencyFormatters = new Map<string, Intl.NumberFormat>()
function currencyFormatter(currency: string): Intl.NumberFormat {
  let formatter = currencyFormatters.get(currency)
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency })
    currencyFormatters.set(currency, formatter)
  }
  return formatter
}

/**
 * Get channel label for display
 * @param channel - Channel identifier
 * @returns Display label
 */
export function getChannelLabel(channel: string | null | undefined): string {
  if (!channel) return '—'
  const channelMap: Record<string, string> = {
    airbnb: 'Airbnb',
    airbnb_official: 'Airbnb',
    vrbo: 'VRBO',
    home_away: 'VRBO',
    direct: 'Direct',
  }
  return channelMap[channel.toLowerCase()] || channel
}

/**
 * Get badge variant for channel
 * @param channel - Channel identifier
 * @returns Badge variant
 */
export function getChannelBadgeVariant(channel: string | null | undefined): string {
  if (channel === 'airbnb' || channel === 'airbnb_official') return 'default'
  if (channel === 'vrbo' || channel === 'home_away') return 'secondary'
  if (channel === 'direct') return 'default'
  return 'secondary'
}

/**
 * Get badge variant for status
 * @param status - Status string
 * @returns Badge variant
 */
export function getStatusBadgeVariant(status: string | null | undefined): string {
  if (!status) return 'secondary'
  const statusLower = status.toLowerCase()
  if (statusLower.includes('check_in') || statusLower.includes('confirmed')) return 'default'
  if (statusLower.includes('check_out')) return 'warning'
  if (statusLower.includes('cancelled')) return 'destructive'
  return 'secondary'
}

/**
 * Calculate number of nights between check-in and check-out
 * @param checkIn - Check-in date
 * @param checkOut - Check-out date
 * @returns Number of nights
 */
export function calculateNights(checkIn: string, checkOut: string): number | null {
  if (!checkIn || !checkOut) return null
  try {
    const start = new Date(checkIn)
    const end = new Date(checkOut)
    return differenceInDays(end, start)
  } catch {
    return null
  }
}

/**
 * Get channel label from message source/channel field
 * Defaults to "Email" if not specified
 * @returns Channel label (Email, SMS, Airbnb, VRBO, etc.)
 */
export function getMessageChannel(message: MessageLike | null | undefined): string {
  if (!message) return 'Email'
  // Check for explicit channel or source field
  const channel = message.channel || message.source
  if (!channel) return 'Email'

  // Map known channels to display labels
  const channelMap: Record<string, string> = {
    email: 'Email',
    sms: 'SMS',
    airbnb: 'Airbnb',
    airbnb_official: 'Airbnb',
    vrbo: 'VRBO',
    homeaway: 'VRBO',
    home_away: 'VRBO',
  }
  return (
    channelMap[channel.toLowerCase()] ||
    channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase()
  )
}

/**
 * Extract guest name from message or fallback
 * Removes email addresses and reservation info from subject/body
 * @param fallbackName - Fallback guest name
 * @returns Guest name
 */
export function getMessageSenderName(
  message: MessageLike | null | undefined,
  fallbackName = 'Guest'
): string {
  if (!message) return fallbackName

  // Try to extract name from 'from' field (e.g., "Rosemary Hoffman <email@example.com>" or just "Michael")
  if (message.from) {
    const fromMatch = message.from.match(/^([^<]+)/)
    if (fromMatch) {
      const name = fromMatch[1].trim()
      // Return the name if it doesn't look like an email address
      if (name && !name.includes('@') && name.length > 0) {
        return name
      }
    }
  }

  return fallbackName
}
